import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch } from 'vue'

import TransportationLocationUpdateListItem from './TransportationLocationUpdateListItem.vue.vue'
import LButton from '@/components/core/Button.vue'
import { Transportation } from '@/types/transportation'
import { LocationAutocompleteResult } from '@/types/trip'

interface TransportationLocationUpdateModalProps {
  value?: boolean
  destinationTransportations: Transportation[]
  originTransportations: Transportation[]
  newLocation: LocationAutocompleteResult
  preselectedTransportationIds?: string[]
}


export default /*#__PURE__*/_defineComponent({
  __name: 'TransportationLocationUpdateModal',
  props: {
    value: { type: Boolean, default: undefined },
    destinationTransportations: null,
    originTransportations: null,
    newLocation: null,
    preselectedTransportationIds: { default: () => [] }
  },
  emits: ["input", "update:transportations"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'input', value: boolean): void
  (
    e: 'update:transportations',
    transportations: {
      transportationId: string
      fieldToUpdate: 'origin' | 'destination'
    }[],
  ): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value: boolean, destinationTransportations: Transportation[], originTransportations: Transportation[], newLocation: LocationAutocompleteResult, preselectedTransportationIds: string[] };





const internalValue = ref(false)
const isOpen = computed({
  get: () => props.value ?? internalValue.value,
  set: (value) => {
    internalValue.value = value
    emit('input', value)
  },
})

const newLocationName = computed(() => {
  return props.newLocation?.title || props.newLocation?.subtitle || ''
})

const selectedTransportations = ref<string[]>([])

// Initialize selectedTransportations with preselected IDs when the modal opens
watch(
  () => props.value,
  (isOpen) => {
    if (isOpen) {
      selectedTransportations.value = [...props.preselectedTransportationIds]
    }
  },
  { immediate: true },
)

function toggleTransportationSelection(transportationId: string) {
  if (selectedTransportations.value.includes(transportationId)) {
    selectedTransportations.value = selectedTransportations.value.filter(
      (id) => id !== transportationId,
    )
  } else {
    selectedTransportations.value.push(transportationId)
  }
}

// Select all functionality ----------------------------------------------------

const areAllDestinationTransportationsSelected = computed(() => {
  if (!props.destinationTransportations.length) {
    return false
  }
  return props.destinationTransportations.every((t) =>
    selectedTransportations.value.includes(t._id),
  )
})

const areAllOriginTransportationsSelected = computed(() => {
  if (!props.originTransportations.length) {
    return false
  }
  return props.originTransportations.every((t) =>
    selectedTransportations.value.includes(t._id),
  )
})

function toggleAllDestinationTransportations() {
  const destinationIds = props.destinationTransportations.map((t) => t._id)

  if (areAllDestinationTransportationsSelected.value) {
    // Remove all destination transportation IDs from selection
    selectedTransportations.value = selectedTransportations.value.filter(
      (id) => !destinationIds.includes(id),
    )
  } else {
    // Add all destination transportation IDs to selection
    destinationIds.forEach((id) => {
      if (!selectedTransportations.value.includes(id)) {
        selectedTransportations.value.push(id)
      }
    })
  }
}

function toggleAllOriginTransportations() {
  const originIds = props.originTransportations.map((t) => t._id)

  if (areAllOriginTransportationsSelected.value) {
    // Remove all origin transportation IDs from selection
    selectedTransportations.value = selectedTransportations.value.filter(
      (id) => !originIds.includes(id),
    )
  } else {
    // Add all origin transportation IDs to selection
    originIds.forEach((id) => {
      if (!selectedTransportations.value.includes(id)) {
        selectedTransportations.value.push(id)
      }
    })
  }
}

function confirmTransportationUpdates() {
  if (selectedTransportations.value.length === 0) {
    return
  }

  const transportationUpdates: {
    transportationId: string
    fieldToUpdate: 'origin' | 'destination'
  }[] = []

  // Collect destination transportations (update destination field)
  for (const transportation of props.destinationTransportations) {
    if (selectedTransportations.value.includes(transportation._id)) {
      transportationUpdates.push({
        transportationId: transportation._id,
        fieldToUpdate: 'destination',
      })
    }
  }

  // Collect origin transportations (update origin field)
  for (const transportation of props.originTransportations) {
    if (selectedTransportations.value.includes(transportation._id)) {
      transportationUpdates.push({
        transportationId: transportation._id,
        fieldToUpdate: 'origin',
      })
    }
  }

  emit('update:transportations', transportationUpdates)
  closeModal()
}

function closeModal() {
  selectedTransportations.value = []
  isOpen.value = false
}

return { __sfc: true,props, emit, internalValue, isOpen, newLocationName, selectedTransportations, toggleTransportationSelection, areAllDestinationTransportationsSelected, areAllOriginTransportationsSelected, toggleAllDestinationTransportations, toggleAllOriginTransportations, confirmTransportationUpdates, closeModal, TransportationLocationUpdateListItem, LButton }
}

})